/* ============================================================
   Fence Works — CSV in and out
   Shared by the Import & export screen, the Jobs Sheet export and the
   cash forecast's bill upload. Plain functions, no DOM except
   downloadCsv, so scripts/unit.mjs can test them in a bare vm.

   Parsing is RFC 4180 plus what real exports throw at it: a UTF-8 BOM
   (Excel), CRLF or bare CR line ends, quoted fields with commas, quotes
   ("") and newlines inside, and semicolon- or tab-separated files
   (European Excel, Google Sheets TSV). The delimiter is sniffed from the
   first line, outside quotes.

   Writing guards against formula injection: a text cell that starts with
   = + - @ (or a tab / CR) is prefixed with ' so a customer named
   "=HYPERLINK(...)" opens in Excel as text, not a live formula. Real
   numbers are written as numbers and never prefixed.
   ============================================================ */

function sniffDelimiter(text: string): string {
  let inQ = false, comma = 0, semi = 0, tab = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') inQ = !inQ;
    else if (!inQ && (ch === "\n" || ch === "\r")) break;
    else if (!inQ && ch === ",") comma++;
    else if (!inQ && ch === ";") semi++;
    else if (!inQ && ch === "\t") tab++;
  }
  if (tab > comma && tab >= semi) return "\t";
  if (semi > comma) return ";";
  return ",";
}

/* text → rows of cells. Blank lines are dropped. */
function parseCsv(text: string, delimiter?: string): string[][] {
  let s = String(text == null ? "" : text);
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
  const d = delimiter || sniffDelimiter(s);
  const rows: string[][] = [];
  let row: string[] = [], cell = "", inQ = false, i = 0;
  const endRow = () => {
    row.push(cell); cell = "";
    if (!(row.length === 1 && row[0] === "")) rows.push(row);
    row = [];
  };
  while (i < s.length) {
    const ch = s[i];
    if (inQ) {
      if (ch === '"') {
        if (s[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      cell += ch; i++; continue;
    }
    if (ch === '"' && cell === "") { inQ = true; i++; continue; }
    if (ch === d) { row.push(cell); cell = ""; i++; continue; }
    if (ch === "\r") { endRow(); i += s[i + 1] === "\n" ? 2 : 1; continue; }
    if (ch === "\n") { endRow(); i++; continue; }
    cell += ch; i++;
  }
  if (cell !== "" || row.length) endRow();
  return rows;
}

/* text → { headers, rows: [{ header: value }] }. Headers are trimmed;
   a repeated header gets " 2", " 3"… so no column is silently lost. */
function csvRecords(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const grid = parseCsv(text);
  if (!grid.length) return { headers: [], rows: [] };
  const seen: Record<string, number> = {};
  const headers = grid[0].map((h, i) => {
    let name = String(h || "").trim() || "Column " + (i + 1);
    const base = name;
    if (seen[base]) { seen[base]++; name = base + " " + seen[base]; } else seen[base] = 1;
    return name;
  });
  const rows = grid.slice(1).map((cells) => {
    const r: Record<string, string> = {};
    headers.forEach((h, i) => { r[h] = String(cells[i] == null ? "" : cells[i]).trim(); });
    return r;
  }).filter((r) => Object.keys(r).some((k) => r[k] !== ""));
  return { headers, rows };
}

/* "Customer Name" / "customer_name" / "CUSTOMER-NAME" → "customername" — for header matching */
function normHeader(h: string): string {
  return String(h || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function csvCell(v: any): string {
  if (v == null) return "";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  if (typeof v === "boolean") return v ? "true" : "false";
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return /[",\r\n]|^\s|\s$/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/* columns: ["key", …] or [{ key, label }, …]; rows: objects. CRLF line ends (Excel's). */
function toCsv(columns: Array<string | { key: string; label?: string }>, rows: any[]): string {
  const cols = columns.map((c) => (typeof c === "string" ? { key: c, label: c } : { key: c.key, label: c.label || c.key }));
  const lines = [cols.map((c) => csvCell(c.label)).join(",")];
  (rows || []).forEach((r) => lines.push(cols.map((c) => csvCell(r == null ? "" : r[c.key])).join(",")));
  return lines.join("\r\n") + "\r\n";
}

/* "$1,234.50" → 1234.5 · "(250)" → -250 · "" / "n/a" → null */
function parseMoney(v: any): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v == null ? "" : v).trim();
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[$€£\s,]/g, "");
  if (s.startsWith("-")) { neg = !neg; s = s.slice(1); }
  if (!/^\d*\.?\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

/* 2026-03-04 · 3/4/2026 · 3/4/26 · "Mar 4, 2026" → "2026-03-04" (local calendar date); anything else → null.
   Slashed dates are read US-style (month first), as every US export writes them. */
function parseDateLoose(v: any): string | null {
  const s = String(v == null ? "" : v).trim();
  if (!s) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const ok = (y: number, m: number, d: number) => {
    if (!(y >= 1900 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d ? y + "-" + pad(m) + "-" + pad(d) : null;
  };
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/.exec(s);
  if (m) return ok(+m[1], +m[2], +m[3]);
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  if (m) { const y = m[3].length === 2 ? 2000 + +m[3] : +m[3]; return ok(y, +m[1], +m[2]); }
  if (/[a-z]/i.test(s)) {
    const t = Date.parse(s);
    if (Number.isFinite(t)) { const d = new Date(t); return ok(d.getFullYear(), d.getMonth() + 1, d.getDate()); }
  }
  return null;
}

/* Browser download. A BOM makes Excel open UTF-8 (names with accents) correctly. */
function downloadCsv(filename: string, csv: string): void {
  if (typeof document === "undefined") return;
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = /\.csv$/i.test(filename) ? filename : filename + ".csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export { sniffDelimiter, parseCsv, csvRecords, normHeader, csvCell, toCsv, parseMoney, parseDateLoose, downloadCsv };
